import { del, get, post, put } from "../../../../GlobalModules/Utility/Http";
import {
    stockTransferCreatePost, stockTransferDeleteDel,
    stockTransferDetailsGet,
    stockTransferListGet,
    stockTransferUpdatePut,
    storeListGet,
    stockTransferDispatchPost
} from "../Model/StockTranferModel";
import { mergeProductPost } from "../Model/ProductModel";

/**
 * Fetch stock transfer list with optional filters
 * @param {Object} filters - Filter parameters for the API call
 * @returns {Promise} Processed stock transfer list data
 */
export const getStockTransferList = async (filters = {}) => {
    // Format filters according to the API structure in Postman collection
    let queryParams = {};

    const filtersObj = {
        limit: filters.limit || 10,
        offset: filters.offset || 0,
    };

    if (filters.store_id) {
        filtersObj.store_id = filters.store_id;
    }

    if (filters.from_created_date) {
        filtersObj.from_created_date = filters.from_created_date;
    }

    if (filters.to_created_date) {
        filtersObj.to_created_date = filters.to_created_date;
    }

    if (filters.search) {
        queryParams.filter_op = JSON.stringify({ search_attr: "substring" });
        filtersObj.search_attr = filters.search;
    }

    if (filters.sort) {
        filtersObj.sort = filters.sort.map(item => ({
            colId: item.field,
            sort: item.sort
        }));
    }

    queryParams.filters = JSON.stringify(filtersObj);

    const response = await get(`${process.env.EXTENDED_URL}/stock_transfer`, queryParams);

    return stockTransferListGet(response);
};

/**
 * Create a new stock transfer
 * @param {Object} stockTransferData - Stock transfer data to create
 * @returns {Promise} Creation response
 */
export const createStockTransfer = async (stockTransferData) => {
    const response = await post(`${process.env.EXTENDED_URL}/stock_transfer`, stockTransferData);
    return stockTransferCreatePost(response);
};

/**
 * Fetch single stock transfer details by ID
 * @param {string|number} id - Stock transfer ID
 * @returns {Promise} Processed stock transfer details
 */
export const getStockTransferDetails = async (id) => {
    const response = await get(`${process.env.EXTENDED_URL}/stock_transfer/${id}`);
    return stockTransferDetailsGet(response);
};

/**
 * Update an existing stock transfer
 * @param {string|number} slack - Stock transfer ID
 * @param {Object} stockTransferData - Updated stock transfer data
 * @returns {Promise} Update response
 */
export const updateStockTransfer = async (slack, stockTransferData) => {
    const response = await put(`${process.env.EXTENDED_URL}/stock_transfer/${slack}`, stockTransferData);
    return stockTransferUpdatePut(response);
};

/**
 * Delete a stock transfer
 * @param {string|number} slack - Stock transfer ID
 * @returns {Promise} Delete response
 */
export const deleteStockTransfer = async (slack) => {
    const response = await del(`${process.env.EXTENDED_URL}/stock_transfer/${slack}`);
    return stockTransferDeleteDel(response);
};

/**
 * Dispatch a stock transfer (deducts source stock, moves into transit 1370)
 * @param {string} slack - Stock transfer slack identifier
 * @returns {Promise} Dispatch response
 */
export const dispatchStockTransfer = async (slack) => {
    const response = await post(`${process.env.EXTENDED_URL}/stock_transfer/${slack}/dispatch`);
    return stockTransferDispatchPost(response);
};

/**
 * Merge and verify a transferred product into destination store stock
 * @param {Object} data - Merge parameters
 * @returns {Promise} Merge response
 */
export const mergeProductStock = async (data) => {
    const response = await post(`${process.env.EXTENDED_URL}/stock_transfer/product/merge`, data);
    return mergeProductPost(response);
};

/**
 * Reject a transferred product
 * @param {string} slack - Product transfer slack
 * @param {Object} data - Rejection reason data
 * @returns {Promise} Rejection response
 */
export const rejectStockTransferProduct = async (slack, data = {}) => {
    const response = await post(`${process.env.EXTENDED_URL}/stock_transfer/product/reject/${slack}`, data);
    return mergeProductPost(response);
};

/**
 * Fetch store list with optional filters
 * @param {Object} filters - Filter parameters for the API call
 * @returns {Promise} Processed store list data
 */
export const getStoreList = async (filters = {}) => {
    // Format filters according to the API structure in Postman collection
    let queryParams = {};

    if (filters) {
        const filtersObj = {
            limit: filters.limit,
            offset: filters.offset || 0,
        };

        if (filters.all) {
            queryParams.all = true;
        }

        if (filters.search) {
            queryParams.filter_op = JSON.stringify({ search_attr: "substring" });
            filtersObj.search_attr = filters.search;
        }

        queryParams.filters = JSON.stringify(filtersObj);
    }

    const response = await get(`${process.env.EXTENDED_URL}/store`, queryParams);
    return storeListGet(response);
};